import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { AwardIcon, ShieldIcon, LockIcon } from 'lucide-react';
import { Language } from '../App';
interface AboutProps {
  language: Language;
}
const content = {
  en: {
    headline: 'A Practice Built on Trust',
    body: 'Pearl Ann Brown is a professional plastic surgery and aesthetic medicine practice operated by licensed physicians in New York State. We specialize in surgical body contouring, facial rejuvenation, and non-surgical aesthetic treatments. Our practice provides personalized, confidential consultations in a private setting, adhering to all state and federal regulations governing medical care and patient privacy.',
    credentials: [{
      icon: ShieldIcon,
      title: 'Licensed',
      description: 'Fully licensed to practice medicine in New York State'
    }, {
      icon: LockIcon,
      title: 'Patient Confidentiality',
      description: 'HIPAA-compliant with complete discretion guaranteed'
    }]
  },
  es: {
    headline: 'Una Práctica Basada en la Confianza',
    body: 'Pearl Ann Brown es una práctica profesional de cirugía plástica y medicina estética operada por médicos con licencia en el Estado de Nueva York. Nos especializamos en contorno corporal quirúrgico, rejuvenecimiento facial y tratamientos estéticos no quirúrgicos. Proporcionamos consultas personalizadas y confidenciales en un entorno privado, cumpliendo con todas las regulaciones estatales y federales que rigen la atención médica y la privacidad del paciente.',
    credentials: [{
      icon: ShieldIcon,
      title: 'Licenciado',
      description: 'Totalmente licenciado para practicar medicina en Nueva York'
    }, {
      icon: LockIcon,
      title: 'Confidencialidad del Paciente',
      description: 'Cumple con HIPAA con discreción completa garantizada'
    }]
  }
};
export function About({
  language
}: AboutProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const t = content[language];
  return <section ref={ref} className="py-24 px-4 sm:px-6 lg:px-8 bg-cream" data-id="element-11">
      <div className="max-w-7xl mx-auto" data-id="element-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-13">
          <motion.div initial={{
          opacity: 0,
          y: 50
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6
        }} className="space-y-6" data-id="element-14">
            <h2 className="text-4xl sm:text-5xl font-bold text-espresso" data-id="element-15">
              {t.headline}
            </h2>
            <p className="text-lg text-espresso/80 leading-relaxed" data-id="element-16">{t.body}</p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 50
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="space-y-6" data-id="element-17">
            {t.credentials.map((credential, index) => {
            const Icon = credential.icon;
            return <motion.div key={credential.title} initial={{
              opacity: 0,
              x: 50
            }} animate={isInView ? {
              opacity: 1,
              x: 0
            } : {}} transition={{
              duration: 0.6,
              delay: 0.3 + index * 0.1
            }} className="bg-white rounded-2xl p-6 shadow-lg border border-pearl/20 hover:shadow-xl transition-shadow" data-id="element-18">
                  <div className="flex items-start gap-4" data-id="element-19">
                    <div className="bg-pearl/20 rounded-full p-3" data-id="element-20">
                      <Icon className="w-6 h-6 text-espresso" data-id="element-21" />
                    </div>
                    <div data-id="element-22">
                      <h3 className="text-xl font-bold text-espresso mb-2" data-id="element-23">
                        {credential.title}
                      </h3>
                      <p className="text-espresso/70" data-id="element-24">
                        {credential.description}
                      </p>
                    </div>
                  </div>
                </motion.div>;
          })}
          </motion.div>
        </div>
      </div>
    </section>;
}